import React, { useEffect, useState, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Phone, ArrowRight, CheckCircle2, X, Loader2 } from 'lucide-react';
declare global {
  interface Window {
    hcaptcha?: {
      reset: (widgetId?: string) => void;
      render: (container: HTMLElement | string, params: Record<string, unknown>) => string;
    };
  }
}
export const ContactSection = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  // Load Web3Forms client script — it auto-renders hCaptcha for [data-captcha="true"]
  // Per https://docs.web3forms.com/getting-started/customizations/spam-protection/hcaptcha
  useEffect(() => {
    if (document.querySelector('script[src*="web3forms.com/client/script.js"]')) {
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://web3forms.com/client/script.js';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }, []);
  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorMessage('');
    const form = event.currentTarget;
    const formData = new FormData(form);
    // Verify hCaptcha was completed
    const captchaToken = formData.get('h-captcha-response');
    if (!captchaToken || typeof captchaToken !== 'string' || !captchaToken.trim()) {
      setErrorMessage('Please complete the hCaptcha before sending.');
      return;
    }
    setIsSubmitting(true);
    // Build JSON payload per Web3Forms docs
    const payload: Record<string, string> = {
      access_key: '0b41ac13-ec8b-408e-8836-6689e10506ea',
      subject: 'New UHD Contact Form Submission'
    };
    formData.forEach((value, key) => {
      if (typeof value === 'string') payload[key] = value;
    });
    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (data.success) {
        setShowSuccess(true);
        form.reset();
        if (window.hcaptcha) {
          try {
            window.hcaptcha.reset();
          } catch {
            // no-op
          }
        }
      } else {
        setErrorMessage(data.message || 'Something went wrong. Please try again.');
        if (window.hcaptcha) {
          try {
            window.hcaptcha.reset();
          } catch {
            // no-op
          }
        }
      }
    } catch (err) {
      setErrorMessage('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  return <section id="contact" className="py-24 bg-navy-800 relative border-t border-navy-700" data-id="element-17" data-mp="30gwqigg5xrq4">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-18" data-mp="2sz6xsex8663d">
        <div className="bg-navy-900 border border-navy-700 rounded-sm overflow-hidden shadow-2xl" data-id="element-19" data-mp="2o5lzsmvmtc46">
          <div className="grid grid-cols-1 lg:grid-cols-2" data-id="element-20" data-mp="2j841rrj5khay">
            {/* Left side - Info */}
            <div className="p-10 md:p-16 bg-gradient-to-br from-navy-900 to-navy-800 relative overflow-hidden" data-id="element-21" data-mp="2f70jkydmu96k">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-ice-500 to-gold-500" data-id="element-22" data-mp="3bvgd15jv52oy"></div>

              <motion.h2 initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} className="text-3xl md:text-4xl font-bold text-white mb-4 uppercase tracking-tight" data-id="element-23" data-mp="36usmekzjqnv9">
                Ready to Take the{' '}
                <span className="text-gold-500" data-id="element-24" data-mp="3acoqexc5r57l">Next Step?</span>
              </motion.h2>
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.1
            }} className="text-slate-300 mb-10 leading-relaxed" data-id="element-25" data-mp="3f4px83qk43q8">
                UHD wants to be known as the developmental and representation
                team that doesn't waste players' and families' time. We provide
                the tools you need to get to the next level.
              </motion.p>

              <div className="space-y-6" data-id="element-26" data-mp="28ncp61igg5ei">
                <motion.div initial={{
                opacity: 0,
                x: -20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: 0.2
              }} className="flex items-center gap-4 text-white" data-id="element-27" data-mp="28qqqd6fdu8kd">
                  <div className="w-12 h-12 bg-navy-800 flex items-center justify-center rounded-sm border border-navy-700" data-id="element-28" data-mp="2a3qgeruut8hg">
                    <Mail className="text-ice-500 w-5 h-5" data-id="element-29" data-mp="1hhe1ji6l9zok" />
                  </div>
                  <div data-id="element-30" data-mp="1w5s2toocxg30">
                    <p className="text-sm text-slate-400 font-semibold uppercase tracking-wider" data-id="element-31" data-mp="1w61tleh87r9r">
                      Email Us
                    </p>
                    <p className="font-medium" data-id="element-32" data-mp="1rcyyv30pqbrz">
                      contact@universalhockeydev.com
                    </p>
                  </div>
                </motion.div>

                <motion.div initial={{
                opacity: 0,
                x: -20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: 0.3
              }} className="flex items-center gap-4 text-white" data-id="element-33" data-mp="ypuzbdke2pnl">
                  <div className="w-12 h-12 bg-navy-800 flex items-center justify-center rounded-sm border border-navy-700" data-id="element-34" data-mp="2fvcmihskpuxq">
                    <Phone className="text-ice-500 w-5 h-5" data-id="element-35" data-mp="2kupgdmhv429t" />
                  </div>
                  <div data-id="element-36" data-mp="2owkhoqzsmfnl">
                    <p className="text-sm text-slate-400 font-semibold uppercase tracking-wider" data-id="element-37" data-mp="2ttja953t6ejo">
                      Call Us
                    </p>
                    <p className="font-medium" data-id="element-38" data-mp="43pdqyqgnrz6">Available upon request</p>
                  </div>
                </motion.div>
              </div>
            </div>

            {/* Right side - Form */}
            <div className="p-10 md:p-16 bg-navy-800" data-id="element-39" data-mp="j6qttrj6zsrf">
              <form className="space-y-6" onSubmit={onSubmit} data-id="element-40" data-mp="e8flxmcgjsbu">
                {/* Honeypot field for spam protection */}
                <input type="checkbox" name="botcheck" className="hidden" style={{
                display: 'none'
              }} tabIndex={-1} autoComplete="off" data-id="element-41" data-mp="9gdz56hnyj7k" />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-42" data-mp="3rm0r8mc509sv">
                  <div data-id="element-43" data-mp="3mnpfu7671r8j">
                    <label htmlFor="firstName" className="block text-sm font-semibold text-slate-300 mb-2 uppercase tracking-wider" data-id="element-44" data-mp="3mp0lkcj7992a">
                      First Name
                    </label>
                    <input type="text" id="firstName" name="firstName" required disabled={isSubmitting} className="w-full bg-navy-900 border border-navy-700 rounded-sm px-4 py-3 text-white focus:outline-none focus:border-ice-500 transition-colors disabled:opacity-60" placeholder="John" data-id="element-45" data-mp="1gv0xeaymmokn" />
                  </div>
                  <div data-id="element-46" data-mp="1vapi9wfsjhyu">
                    <label htmlFor="lastName" className="block text-sm font-semibold text-slate-300 mb-2 uppercase tracking-wider" data-id="element-47" data-mp="1gg1fiq1ryxmx">
                      Last Name
                    </label>
                    <input type="text" id="lastName" name="lastName" required disabled={isSubmitting} className="w-full bg-navy-900 border border-navy-700 rounded-sm px-4 py-3 text-white focus:outline-none focus:border-ice-500 transition-colors disabled:opacity-60" placeholder="Doe" data-id="element-48" data-mp="17gxuvhmyma6b" />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-49" data-mp="3buyfjdi6hyls">
                  <div data-id="element-50" data-mp="3pp9865xaqcx4">
                    <label htmlFor="email" className="block text-sm font-semibold text-slate-300 mb-2 uppercase tracking-wider" data-id="element-51" data-mp="3pqkd492h9qpt">
                      Email Address
                    </label>
                    <input type="email" id="email" name="email" required disabled={isSubmitting} className="w-full bg-navy-900 border border-navy-700 rounded-sm px-4 py-3 text-white focus:outline-none focus:border-ice-500 transition-colors disabled:opacity-60" placeholder="john@example.com" data-id="element-52" data-mp="3uoc6l7irtkw4" />
                  </div>
                  <div data-id="element-53" data-mp="ydl33yjrm8cd">
                    <label htmlFor="phone" className="block text-sm font-semibold text-slate-300 mb-2 uppercase tracking-wider" data-id="element-54" data-mp="ye48ygw556a1">
                      Phone
                    </label>
                    <input type="tel" id="phone" name="phone" disabled={isSubmitting} className="w-full bg-navy-900 border border-navy-700 rounded-sm px-4 py-3 text-white focus:outline-none focus:border-ice-500 transition-colors disabled:opacity-60" placeholder="(555) 123-4567" data-id="element-55" data-mp="kdjsvshm1ok8" />
                  </div>
                </div>

                <div data-id="element-56" data-mp="13abh8gc0etv5">
                  <label htmlFor="level" className="block text-sm font-semibold text-slate-300 mb-2 uppercase tracking-wider" data-id="element-57" data-mp="20rjncnxosm03">
                    Current Level of Play
                  </label>
                  <select id="level" name="level" required disabled={isSubmitting} className="w-full bg-navy-900 border border-navy-700 rounded-sm px-4 py-3 text-white focus:outline-none focus:border-ice-500 transition-colors appearance-none disabled:opacity-60" data-id="element-58" data-mp="23dsr27d99jyk">
                    <option value="" data-id="element-59" data-mp="2mac437r6js0e">Select Level</option>
                    <option value="youth" data-id="element-60" data-mp="2r9x3tke6c4e6">Youth (U10-U18)</option>
                    <option value="junior" data-id="element-61" data-mp="2r8dphzc2w3f1">Junior</option>
                    <option value="college" data-id="element-62" data-mp="2w0n96xmlo78v">College (NCAA/ACHA)</option>
                    <option value="pro" data-id="element-63" data-mp="3100322bw2eky">Professional</option>
                  </select>
                </div>

                <div data-id="element-64" data-mp="35zcoqjr0xfly">
                  <label htmlFor="message" className="block text-sm font-semibold text-slate-300 mb-2 uppercase tracking-wider" data-id="element-65" data-mp="3604dpbq48f39">
                    Message
                  </label>
                  <textarea id="message" name="message" rows={4} required disabled={isSubmitting} className="w-full bg-navy-900 border border-navy-700 rounded-sm px-4 py-3 text-white focus:outline-none focus:border-ice-500 transition-colors resize-none disabled:opacity-60" placeholder="How can we help you?" data-id="element-66" data-mp="1attypuj0kstp"></textarea>
                </div>

                {/* hCaptcha — auto-rendered by Web3Forms client script via data-captcha="true" */}
                <div data-id="element-67" data-mp="22kzy28qwumhx">
                  <label className="block text-sm font-semibold text-slate-300 mb-2 uppercase tracking-wider" data-id="element-68" data-mp="1ylsaefmdkq7d">
                    hCaptcha Verification
                  </label>
                  <div className="h-captcha" data-captcha="true" data-theme="dark" data-id="element-69" data-mp="8awcvtz65yuc"></div>
                  <p className="text-xs text-slate-400 mt-2" data-id="element-70" data-mp="3q9gvfn8dhgqf">
                    Protected by hCaptcha — please complete to send your
                    message.
                  </p>
                </div>

                {errorMessage && <div role="alert" className="border border-red-500/40 bg-red-500/10 text-red-300 text-sm px-4 py-3 rounded-sm" data-id="element-71" data-mp="3li8k2lnhflam">
                    {errorMessage}
                  </div>}

                <button type="submit" disabled={isSubmitting} className="w-full bg-gold-500 hover:bg-gold-400 disabled:opacity-70 disabled:cursor-not-allowed text-navy-900 font-bold text-lg py-4 rounded-sm transition-colors flex items-center justify-center gap-2" data-id="element-72" data-mp="3blcbkfxl2z62">
                  {isSubmitting ? <>
                      <Loader2 className="w-5 h-5 animate-spin" data-id="element-73" data-mp="10r830d8zezsx" />
                      Sending...
                    </> : <>
                      Send Message
                      <ArrowRight className="w-5 h-5" data-id="element-74" data-mp="2rtyku6eudy2t" />
                    </>}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      <AnimatePresence data-id="element-75" data-mp="2dwa2b588z30x">
        {showSuccess && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy-900/80 backdrop-blur-sm" onClick={() => setShowSuccess(false)} role="dialog" aria-modal="true" aria-labelledby="success-title" data-id="element-76" data-mp="2dxuuwpb04bw5">
            <motion.div initial={{
          opacity: 0,
          scale: 0.9,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 10
        }} transition={{
          type: 'spring',
          stiffness: 260,
          damping: 22
        }} onClick={e => e.stopPropagation()} className="relative bg-navy-800 border border-navy-700 rounded-sm shadow-2xl max-w-md w-full p-8 text-center" data-id="element-77" data-mp="1q7queqt40s9d">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-ice-500 to-gold-500" data-id="element-78" data-mp="3s2oj4nbrevjc"></div>

              <button type="button" onClick={() => setShowSuccess(false)} aria-label="Close" className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors" data-id="element-79" data-mp="qv8os4w46g2">
                <X className="w-5 h-5" data-id="element-80" data-mp="bjdf9dr1ggqo" />
              </button>

              <div className="flex justify-center mb-5" data-id="element-81" data-mp="bhu39zcf0rup">
                <div className="w-16 h-16 rounded-full bg-gold-500/10 border border-gold-500/30 flex items-center justify-center" data-id="element-82" data-mp="6kajkjwy7mvd">
                  <CheckCircle2 className="w-9 h-9 text-gold-500" data-id="element-83" data-mp="kebd8z91mjov" />
                </div>
              </div>

              <h3 id="success-title" className="text-2xl font-bold text-white mb-3 uppercase tracking-tight" data-id="element-84" data-mp="3oqoyom6ye2vc">
                Message Sent
              </h3>
              <p className="text-slate-300 leading-relaxed mb-6" data-id="element-85" data-mp="1lzd8j5v3uhw">
                Your message has been sent to our staff. We'll be in touch with
                you shortly.
              </p>

              <button type="button" onClick={() => setShowSuccess(false)} className="w-full bg-gold-500 hover:bg-gold-400 text-navy-900 font-bold py-3 rounded-sm transition-colors" data-id="element-86" data-mp="1d15vrqmav8zp">
                Close
              </button>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </section>;
};